"use client";

import React from "react";
import Link from "next/link";

/**
 * LP バージョンB - コンテンツ部分
 * ヘッダー・フッターなしのセクションのみ
 */

function HeroSection() {
  return (
    <section className="bg-gradient-to-br from-orange-400 to-primary-600 text-white pt-24 pb-32 px-6 relative overflow-hidden">
      {/* 背景装飾 */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl -ml-20 -mb-20" />

      <div className="max-w-4xl mx-auto text-center relative z-10 animate-fade-in">
        <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl mb-8 leading-tight drop-shadow-sm">
          「できる」はずなのに、<br className="hidden md:block" />
          評価が追いつかないあなたへ。
        </h1>
        <p className="text-lg md:text-xl text-orange-50 mb-12 font-medium leading-relaxed max-w-2xl mx-auto opacity-95">
          Truemeeは、あなたを飾るためのツールではありません。<br />
          埋もれている「本当の価値」を言葉にし、<br className="hidden md:block" />
          キャリアのモヤモヤを解消するための整理場です。
        </p>
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <a
            href="#analysis"
            className="bg-white text-primary-600 px-12 py-5 text-lg font-bold rounded-full shadow-xl hover:bg-orange-50 transition-all duration-300"
          >
            自分の経歴を客観的に整理する
          </a>
        </div>
      </div>
    </section>
  );
}

function ProblemSection() {
  const problems = [
    {
      num: "01",
      title: "言語化の壁",
      desc: "「やってきたこと」は言えるが、「価値」に変換できていない。業務の羅列だけでは、あなたの独自の思考プロセスが伝わりません。"
    },
    {
      num: "02",
      title: "相手目線の欠如",
      desc: "あなたの強みと、企業が求めているものがズレている。第三者の視点で整理することで、マッチングの精度は劇的に変わります。"
    },
    {
      num: "03",
      title: "自信の揺らぎ",
      desc: "不合格の理由が分からず、能力を疑ってしまう。伝え方が噛み合っていないだけの可能性を特定し、自信を取り戻します。"
    }
  ];

  return (
    <section id="analysis" className="py-24 px-6 bg-gradient-to-b from-orange-50 to-white">
      <div className="max-w-5xl mx-auto text-center">
        <p className="text-xs font-bold text-primary-500 tracking-[0.3em] uppercase mb-4">Empathy & Insight</p>
        <h2 className="font-serif text-2xl md:text-3xl font-bold mb-16">その評価の「ズレ」には、理由があります。</h2>
        <div className="grid md:grid-cols-3 gap-6 text-left">
          {problems.map((p) => (
            <div key={p.num} className="bg-white p-10 rounded-2xl shadow-lg border border-orange-50/50">
              <div className="w-10 h-10 bg-orange-100 text-primary-600 rounded-lg flex items-center justify-center font-bold mb-6">
                {p.num}
              </div>
              <h3 className="font-bold text-lg mb-4 text-orange-950">{p.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PhilosophySection() {
  return (
    <section id="philosophy" className="py-24 px-6 bg-orange-50/30">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-serif text-2xl md:text-4xl font-bold mb-16 leading-relaxed text-orange-900">
          「下駄を履かせない」からこそ、<br />
          一生モノの整理ができる。
        </h2>
        <div className="grid md:grid-cols-2 gap-8 text-left">
          <div className="bg-white p-8 rounded-2xl border border-orange-100">
            <h3 className="text-lg font-bold mb-4 text-primary-600 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              実力以上の装飾はしません
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              私たちは、あなたを「良く見せる」のではなく「正しく見せる」ことにこだわります。入社後の幸福なマッチングのためには、ありのままの整理が不可欠だからです。
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-orange-100">
            <h3 className="text-lg font-bold mb-4 text-primary-600 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              「成功」をゴールにしません
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              大切なのは、あなたが自分の経歴を自分の言葉で語れるようになること。その結果として、内定や納得感のある選択肢がついてくるのだと考えます。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValueSection() {
  const values = [
    { letter: "A", title: "評価の健康診断", desc: "市場や企業から見て、どこが「急所」でどこが「評価点」になるかを客観的に可視化します。" },
    { letter: "B", title: "面接の「壁」の特定", desc: "必ず突っ込まれる「論理的弱点」を事前に特定。面接への不安を自信に変えます。" },
    { letter: "C", title: "納得感のあるキャリア整理", desc: "自分の強みを言葉にすることで、これからの仕事への向き合い方がクリアになります。" }
  ];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-center font-serif text-3xl font-bold mb-16">Truemeeで手に入る「地図」</h2>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            {values.map((v) => (
              <div key={v.letter} className="flex gap-6 items-start group">
                <div className="w-12 h-12 bg-orange-100 text-primary-600 rounded-xl flex items-center justify-center shrink-0 font-bold group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                  {v.letter}
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">{v.title}</h3>
                  <p className="text-sm text-slate-500">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-orange-50/50 p-12 rounded-[2.5rem] border border-orange-100 text-center relative overflow-hidden">
            <div className="text-5xl mb-6">🧭</div>
            <h3 className="font-serif font-bold text-2xl mb-4 text-orange-900">
              まずは「現在地」を<br />知ることから。
            </h3>
            <p className="text-sm text-orange-800/60 mb-8 leading-relaxed">
              無料で、あなたの経歴がプロの視点で<br />どう映るかをシミュレーションできます。
            </p>
            <Link
              href="/auth/register"
              className="inline-block bg-primary-600 text-white px-10 py-4 rounded-full font-bold shadow-lg hover:bg-primary-700 hover:-translate-y-0.5 transition-all duration-300"
            >
              無料で整理を始める
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function TargetSection() {
  const targets = [
    "書類は通るが、面接での評価が割れやすい",
    "実績はあるのに、伝え方が分からず損をしている",
    "不合格の理由が分からず、対策の立てようがない",
    "エージェントではない、第三者の客観的な意見が欲しい"
  ];

  return (
    <section id="target" className="py-24 px-6 bg-gradient-to-b from-orange-50 to-white">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-serif text-2xl md:text-3xl font-bold mb-12">このような悩みを持つ方のための場所です</h2>
        <div className="grid md:grid-cols-2 gap-4 text-left">
          {targets.map((t, i) => (
            <div key={i} className="bg-white p-6 rounded-2xl border border-orange-100/60 shadow-lg flex items-start gap-4">
              <span className="text-orange-400 mt-1">●</span>
              <p className="text-slate-700 text-sm font-medium">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section
      className="py-48 px-6 text-center relative bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(255, 252, 248, 0.6) 0%, rgba(255, 252, 248, 0.85) 100%), url('/images/shutterstock_2622315995.jpg')`
      }}
    >
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="bg-gradient-radial from-white/80 to-transparent p-8 rounded-full inline-block">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-10 leading-relaxed text-orange-950">
            あなたのキャリアを、<br className="md:hidden" />もっとクリアに。
          </h2>
          <div className="mb-12">
            <Link
              href="/auth/register"
              className="inline-block bg-primary-600 text-white px-16 py-6 text-xl font-bold rounded-full shadow-2xl hover:bg-primary-700 hover:scale-105 transition-all duration-300"
            >
              自分の評価を整理してみる
            </Link>
          </div>
          <p className="text-base text-orange-900 font-bold tracking-widest">
            Truemeeは、あなたの「なぜ？」に伴走するパートナーです。
          </p>
        </div>
      </div>
    </section>
  );
}

export default function LpVersionBContent() {
  return (
    <>
      <HeroSection />
      <ProblemSection />
      <PhilosophySection />
      <ValueSection />
      <TargetSection />
      <CTASection />
    </>
  );
}
